import cx from "classnames"
import { memo, useState, useEffect } from "react"
import Image from "next/image"
import { Href } from "components/navigation"
import { useMedia } from "src/contexts/device"

// Image sources for different logo variants
const LOGO_IMAGES = {
  mobile: "/images/logo-mob.webp",
  desktop: "/images/logo.webp",
  secondary: "/images/logo.webp",
} as const

const LOGO_DIMENSIONS = {
  mobilePrimary: { width: 38.742, height: 22.177 },
  standard: { width: 105.93, height: 22.177 },
} as const

interface LogoProps {
  className?: string
  isSecondary?: boolean
}

const Logo = memo<LogoProps>(({ className, isSecondary = false }) => {
  const { isMobileView } = useMedia()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const getImageSource = () => {
    if (isSecondary) {
      return LOGO_IMAGES.secondary
    }

    // Default to desktop during SSR, then switch to mobile if needed
    if (!isClient) {
      return LOGO_IMAGES.desktop
    }

    return isMobileView ? LOGO_IMAGES.mobile : LOGO_IMAGES.desktop
  }

  const getDimensions = () => {
    if (isMobileView && !isSecondary && isClient) {
      return LOGO_DIMENSIONS.mobilePrimary
    }

    return LOGO_DIMENSIONS.standard
  }

  const imageSrc = getImageSource()
  const dimensions = getDimensions()

  return (
    <Href to="/" className={cx("flex items-center", className)}>
      <Image
        src={imageSrc}
        alt="Logo"
        width={dimensions.width}
        height={dimensions.height}
        priority={!isSecondary}
        quality={75}
        style={{
          width: `${dimensions.width}px`,
          height: `${dimensions.height}px`,
        }}
        sizes="(max-width: 768px) 38.742px, 105.93px"
      />
    </Href>
  )
})

Logo.displayName = "Logo"

export default Logo
